import * as Domains from './domains';
import * as Errors from './errors';
import * as Location from './location';
import { ConfigUrl, Type, TypeMapping } from './types';

export type RedirectInterceptor = (
  authTypeMapping: TypeMapping,
  url: string,
) => string | null;

export const TYPE_EXTERNAL: Type = 'external';
export const TYPE_INTERNAL: Type = 'internal';
export const TYPES = [TYPE_EXTERNAL, TYPE_INTERNAL];

// use this query parameter to force the library into a specific auth type when
// the host is localhost
const LOCALHOST_AUTH_TYPE_PARAM = 'localhostAuthType';
export const LOCALHOST_AUTH_TYPE_STORAGE_KEY = 'nitro.auth.type';

/**
 * Returns the current url with the domain in `fromAuthTypeMapping` replaced
 * with the domain in `toAuthTypeMapping`.
 *
 * @param location
 * @param fromAuthTypeMapping
 * @param toAuthTypeMapping
 */
function redirectUrl(
  location: Location,
  fromAuthTypeMapping: TypeMapping,
  toAuthTypeMapping: TypeMapping,
) {
  const url = new URL(location.href);
  const subdomain = Domains.subdomainFromFqdn(url.hostname, fromAuthTypeMapping.domain);
  url.hostname = `${subdomain}.${toAuthTypeMapping.domain}`;
  return url.toString();
}

/**
 * Return the first type TypeMapping that is for a type that is in `types`.
 *
 * Will throw an error if a type mapping cannot be found.
 *
 * @param typeMappings
 * @param types
 */
function findTypeMapping(typeMappings: TypeMapping[], types: Type[]): TypeMapping {
  const authTypeMapping = typeMappings.find((m) => types.includes(m.type));
  if (!authTypeMapping) {
    const typesStr = types.join(', ');
    throw new Error(
      `Could not find type mapping for ${typesStr}. Check type mappings and configured types.`,
    );
  }
  return authTypeMapping;
}

/**
 * Returns the auth type for localhost. If it's set in a query param, will
 * return that value. Will cache the auth type in local storage and return
 * it from there if the query param is unavailable.
 *
 * If the auth type is not available from a query param or local storage,
 * returns undefined.
 */
function localhostAuthType(location: Location, storage?: Storage): Type | undefined {
  const paramValue = Location.queryParamValue(location, LOCALHOST_AUTH_TYPE_PARAM);
  const storageValue = storage?.getItem(LOCALHOST_AUTH_TYPE_STORAGE_KEY) ?? undefined;
  let value = paramValue;
  if (value) {
    console.log(`Using ${value} as auth type, from query param.`);
  } else {
    // use the cached local auth type (which may be null) if the param one isn't there
    value = storageValue;
    if (value) {
      console.log(`Using ${value} as auth type, from local storage.`);
    }
  }
  if (paramValue && paramValue !== storageValue) {
    if (storage) {
      // if there was a param auth type, cache it in local storage
      storage.setItem(LOCALHOST_AUTH_TYPE_STORAGE_KEY, paramValue);
      console.log(`Updating local storage with ${value} as localhost auth type.`);
    } else {
      console.warn('Cannot storage localhost auth type. No storage.');
    }
  }
  // verify the value is a proper type
  return TYPES.find((t) => t === value);
}

/**
 * Figure out what type of auth to use (internal or external).
 *
 * @param location
 * @param storage
 * @param typeMappings The mappings from host to type.
 * @param availableTypes Which types are available for the current usage.
 */
function determineAuthType(
  location: Location,
  storage: Storage | undefined,
  typeMappings: TypeMapping[],
  availableTypes: Type[],
): TypeMapping | undefined {
  for (const authTypeMapping of typeMappings) {
    if (authTypeMapping.hostRegex.test(location.hostname)) {
      return authTypeMapping;
    }
  }
  if (Location.isLocalhost(location)) {
    // localhost can't be mapped automatically to a type, because it's not a
    // proper domain, however it is useful during development to be able to use
    // any auth type...so the auth type can be forced via a query parameter,
    // but only for localhost
    const authType = localhostAuthType(location, storage);
    if (authType) {
      return findTypeMapping(typeMappings, [authType]);
    }
    // if no query param directing which type of auth type to use, use
    // the first available
    return findTypeMapping(typeMappings, availableTypes);
  }
  throw new Error(`Could not determine auth type from host '${location.hostname}'.`);
}

export interface AuthTypeOptions {
  // passed in Location, not meant to be overridden, but an explicit dependency to facilitate testing
  location: Location;
  // if redirectOnUnsupportedType is true and the redirect happens, this will
  // be passed the type mapping the user is supposed to be using and the url
  // they are being redirect to
  // - if a url is returned, that url will be used to redirect instead
  // - if null is returned, the redirect doesn't happen
  redirectInterceptor?: RedirectInterceptor;
  // pass true to automatically redirect to the expected url for a supported
  // type if the current url is not supported
  redirectOnUnsupportedType?: boolean;
  // passed in Storage, not meant to be overridden, but an explicit dependency to facilitate testing
  storage?: Storage;
  // a list of types that the current site actually supports
  supportedTypes: Type[];
  // a full set of type mappings for all auth types, it is intended that this
  // include even auth types that aren't supported to allow redirects to
  // happen
  typeMappings: TypeMapping[];
}

/**
 * Provides support for managing a type of auth, for example external or
 * internal.
 *
 * This is its own separate functionality to insulate the auth services from
 * having to know everything about every auth service. Since this is responsible
 * for checking that the auth type is appropriate and redirecting to other
 * types, the auth service can be instantiated and used blindly without issue.
 */
export default class AuthType {
  private readonly location: Location;
  private readonly redirectInterceptor: RedirectInterceptor | undefined;
  private readonly redirectOnUnsupportedType: boolean;
  private readonly storage?: Storage;
  private readonly supportedTypes: Type[];
  private readonly typeMappings: TypeMapping[];
  readonly typeMapping: TypeMapping;

  constructor(options: AuthTypeOptions) {
    this.location = options.location;
    this.storage = options.storage;
    // determines what type of auth the user is supposed to be using based
    // on the current hostname
    const typeMapping = determineAuthType(
      this.location,
      this.storage,
      options.typeMappings,
      options.supportedTypes,
    );
    if (!typeMapping) {
      throw new Error('Could not determine type mapping.');
    }
    this.redirectInterceptor = options.redirectInterceptor;
    this.redirectOnUnsupportedType = options.redirectOnUnsupportedType ?? false;
    this.supportedTypes = options.supportedTypes;
    this.typeMapping = typeMapping;
    this.typeMappings = options.typeMappings;

    this.checkSupported();
  }

  /**
   * Will redirect the user to the proper url for the first supported type
   * available, if redirectOnUnsupportedType is true.
   *
   * If redirectOnUnsupportedType is false, this will error.
   */
  private handleUnsupportedType() {
    if (this.redirectOnUnsupportedType) {
      let url: string | null = redirectUrl(
        this.location,
        this.typeMapping,
        findTypeMapping(this.typeMappings, this.supportedTypes),
      );
      if (this.redirectInterceptor) {
        url = this.redirectInterceptor(this.typeMapping, url);
      }
      if (url) {
        this.location.assign(url);
      }
    }
    throw new Errors.UnsupportedAuthTypeError(this.typeMapping.type);
  }

  /**
   * Checks that what was determined to be the proper auth type is actually
   * one of the auth types that the user is allowed to use.
   */
  private checkSupported() {
    if (this.supportedTypes.includes(this.typeMapping.type)) {
      return;
    }
    this.handleUnsupportedType();
  }

  /**
   * Build a redirect url from the current location's url to one appropriate
   * for the passed `type`.
   *
   * @param type
   */
  redirectUrlToType(type: Type): ConfigUrl {
    const toTypeMapping = findTypeMapping(this.typeMappings, [type]);
    return redirectUrl(this.location, this.typeMapping, toTypeMapping);
  }
}
